// components/bus-finder/operators/OperatorsManagement.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiSearch,
  FiEdit,
  FiTrash2,
  FiEye,
  FiPlus,
  FiUser,
  FiCheckCircle,
  FiXCircle,
  FiExternalLink,
  FiLoader,
  FiFilter,
  FiRefreshCw,
  FiMap,
  FiGlobe
} from 'react-icons/fi';
import BaseUrl from '../../../Url/BaseUrl';
import {
  setSearchParams as setSearchParamsAction,
  setOperators as setOperatorsAction,
  setSearchPerformed as setSearchPerformedAction,
  resetOperatorsSearch,
} from '../../../redux/Slices/busOperatorsSlice';

const OperatorsManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // ---- persisted from redux ----
  const { searchParams, operators, searchPerformed } = useSelector(
    (state) => state.busOperators
  );

  // ---- local-only UI state ----
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [states, setStates] = useState([]);
  const [zones, setZones] = useState([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);

  const resultsRef = useRef(null);

  useEffect(() => {
    // Scroll to results when search is performed and loading is finished
    if (searchPerformed && !loading && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchPerformed, loading]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const statesResponse = await fetch(`${BaseUrl.baseurl}States`, {
          headers: { 'accept': 'text/plain' }
        });
        const statesData = await statesResponse.json();
        setStates(statesData);

        const zonesResponse = await fetch(`${BaseUrl.baseurl}Zones`, {
          headers: { 'accept': 'text/plain' }
        });
        const zonesData = await zonesResponse.json();
        setZones(zonesData);

        setMasterDataLoading(false);
      } catch (error) {
        console.error('Error fetching master data:', error);
        setMasterDataLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 3000);
  };

  const sortOperatorsAlphabetically = (ops) => {
    return [...ops].sort((a, b) => (a.operatorName || '').localeCompare(b.operatorName || ''));
  };

  // Helper to format contact numbers for display (strip dial code)
  const formatContactNumbers = (numbers) => {
    if (!numbers || numbers.length === 0) return t('na', 'N/A');

    const stripDialCode = (num) => {
      if (num.startsWith('+91')) return num.substring(3);
      if (num.startsWith('+')) return num.substring(1).replace(/^\d{1,4}/, '');
      return num;
    };

    const formatted = numbers.map(num => stripDialCode(num));
    return formatted.join(', ');
  };

  const handleSearch = async () => {
    setLoading(true);
    dispatch(setSearchPerformedAction(true));

    const payload = {};
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key].trim() !== '') {
        payload[key] = searchParams[key].trim();
      }
    });

    try {
      const response = await fetch(`${BaseUrl.baseurl}BusOperators/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Search endpoint not available');

      const data = await response.json();
      const sorted = sortOperatorsAlphabetically(data || []);
      dispatch(setOperatorsAction(sorted));

      if (sorted.length === 0) {
        showNotification('info', t('noOperatorsFound', 'No operators found matching your search criteria.'));
      }
    } catch (error) {
      console.error('Error searching operators:', error);

      try {
        const allOperatorsResponse = await fetch(`${BaseUrl.baseurl}BusOperators`, {
          headers: { 'accept': 'text/plain' }
        });
        const allOperators = await allOperatorsResponse.json();

        const filtered = allOperators.filter(operator => {
          const matchesOperatorName = !searchParams.operatorName ||
            operator.operatorName.toLowerCase().includes(searchParams.operatorName.toLowerCase());

          const matchesOperatorNumber = !searchParams.operatorNumber ||
            operator.operatorNumber.toLowerCase().includes(searchParams.operatorNumber.toLowerCase());

          const matchesStateCode = !searchParams.stateCode ||
            operator.stateCode === searchParams.stateCode;

          const matchesZoneCode = !searchParams.zoneCode ||
            operator.zoneCode === searchParams.zoneCode;

          return matchesOperatorName && matchesOperatorNumber && matchesStateCode && matchesZoneCode;
        });

        const sortedFiltered = sortOperatorsAlphabetically(filtered);
        dispatch(setOperatorsAction(sortedFiltered));

        if (sortedFiltered.length === 0) {
          showNotification('info', t('noOperatorsFound', 'No operators found matching your search criteria.'));
        }
      } catch (fallbackError) {
        console.error('Error fetching all operators:', fallbackError);
        showNotification('error', t('searchError', 'Error searching operators. Please try again.'));
        dispatch(setOperatorsAction([]));
      }
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    dispatch(resetOperatorsSearch());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(setSearchParamsAction({ ...searchParams, [name]: value }));
  };

  const handleStateSelect = (stateName) => {
    const selectedState = states.find(state => state.stateName === stateName);
    dispatch(
      setSearchParamsAction({
        ...searchParams,
        stateName,
        stateCode: selectedState?.stateCode || '',
      })
    );
  };

  const handleZoneSelect = (zoneName) => {
    const selectedZone = zones.find(zone => zone.zoneName === zoneName);
    dispatch(
      setSearchParamsAction({
        ...searchParams,
        zoneName,
        zoneCode: selectedZone?.zoneCode || '',
      })
    );
  };

  const handleViewDetails = (operatorNumber) => {
    navigate(`/bus-finder/operators/view/${operatorNumber}`);
  };

  const handleEdit = (operatorNumber) => {
    navigate(`/bus-finder/operators/edit/${operatorNumber}`);
  };

  // ⬇⬇⬇ Clear persisted state, THEN navigate back
  const handleBack = () => {
    dispatch(resetOperatorsSearch());
    navigate('/busfinderpanel');
  };

  const handleDelete = async (operator) => {
    if (window.confirm(t('deactivateConfirm', 'Are you sure you want to deactivate this operator? This will set the status to inactive.'))) {
      try {
        const updatedOperator = {
          ...operator,
          status: false
        };

        const response = await fetch(`${BaseUrl.baseurl}BusOperators/${operator.operatorNumber}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'text/plain'
          },
          body: JSON.stringify(updatedOperator)
        });

        if (response.ok) {
          dispatch(
            setOperatorsAction(
              operators.map(op =>
                op.operatorNumber === operator.operatorNumber
                  ? { ...op, status: false }
                  : op
              )
            )
          );
          showNotification('success', t('deactivateSuccess', 'Operator deactivated successfully (status set to inactive)'));
        } else {
          throw new Error(t('deactivateFailed', 'Failed to deactivate operator'));
        }
      } catch (error) {
        console.error('Error deactivating operator:', error);
        showNotification('error', t('deactivateError', 'Error deactivating operator. Please try again.'));
      }
    }
  };

  const toggleStatus = async (operatorNumber) => {
    try {
      const operator = operators.find(op => op.operatorNumber === operatorNumber);
      const updatedStatus = !operator.status;

      const updatedOperator = {
        ...operator,
        status: updatedStatus
      };

      const response = await fetch(`${BaseUrl.baseurl}BusOperators/${operatorNumber}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(updatedOperator)
      });

      if (response.ok) {
        dispatch(
          setOperatorsAction(
            operators.map(op =>
              op.operatorNumber === operatorNumber
                ? { ...op, status: updatedStatus }
                : op
            )
          )
        );
        showNotification('success', t('statusUpdated', 'Operator {{status}} successfully!', {
          status: updatedStatus ? t('activated') : t('deactivated')
        }));
      } else {
        throw new Error(t('updateStatusFailed', 'Failed to update operator status'));
      }
    } catch (error) {
      console.error('Error updating operator status:', error);
      showNotification('error', t('updateStatusError', 'Error updating operator status. Please try again.'));
    }
  };

  const calculateStats = () => {
    const total = operators.length;
    const active = operators.filter(op => op.status).length;
    const inactive = total - active;
    return { total, active, inactive };
  };

  const stats = calculateStats();

  if (masterDataLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiLoader className="spinning" /> {t('loadingMasterData', 'Loading master data...')}
        </div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        {/* changed to handleBack */}
        <button className="btn-back" onClick={handleBack}>
          ← Back
        </button>
        <h1><FiUser /> {t('busOperatorsManagement', 'Bus Operators Management')}</h1>
        <p>{t('searchAndManageOperators', 'Search and manage all bus operators in the system')}</p>
      </div>

      {notification.message && (
        <div className={`alert alert-${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: '', message: '' })}>×</button>
        </div>
      )}

      <div className="search-section">
        <h3>{t('searchOperators', 'Search Operators')}</h3>
        <div className="detailed-search-grid">
          <div className="search-field-group">
            <label>{t('operatorName', 'Operator Name')}</label>
            <input
              type="text"
              name="operatorName"
              value={searchParams.operatorName}
              onChange={handleInputChange}
              className="search-input"
              placeholder={t('enterOperatorName', 'Enter operator name')}
            />
          </div>

          <div className="search-field-group">
            <label>{t('operatorNumber', 'Operator Number')}</label>
            <input
              type="text"
              name="operatorNumber"
              value={searchParams.operatorNumber}
              onChange={handleInputChange}
              className="search-input"
              placeholder={t('enterOperatorNumber', 'Enter operator number')}
            />
          </div>

          <div className="search-field-group">
            <label>{t('stateName', 'State Name')}</label>
            <select
              className="search-select"
              name="stateName"
              value={searchParams.stateName}
              onChange={(e) => handleStateSelect(e.target.value)}
            >
              <option value="">{t('selectState', 'Select State')}</option>
              {states.map((state) => (
                <option key={state.stateCode} value={state.stateName}>
                  {state.stateName} ({state.stateCode})
                </option>
              ))}
            </select>
          </div>

          <div className="search-field-group">
            <label>{t('stateCode', 'State Code')}</label>
            <input
              type="text"
              name="stateCode"
              value={searchParams.stateCode}
              readOnly
              className="search-input"
              placeholder={t('autoFilledState', 'Auto-filled when selecting state')}
            />
          </div>

          <div className="search-field-group">
            <label>{t('zoneName', 'Zone Name')}</label>
            <select
              className="search-select"
              name="zoneName"
              value={searchParams.zoneName}
              onChange={(e) => handleZoneSelect(e.target.value)}
            >
              <option value="">{t('selectZone', 'Select Zone')}</option>
              {zones.map((zone) => (
                <option key={zone.zoneCode} value={zone.zoneName}>
                  {zone.zoneName} ({zone.zoneCode})
                </option>
              ))}
            </select>
          </div>

          <div className="search-field-group">
            <label>{t('zoneCode', 'Zone Code')}</label>
            <input
              type="text"
              name="zoneCode"
              value={searchParams.zoneCode}
              readOnly
              className="search-input"
              placeholder={t('autoFilledZone', 'Auto-filled when selecting zone')}
            />
          </div>
        </div>

        <div className="search-actions">
          <div className="search-button-group">
            <button
              className="btn-primary"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FiLoader className="spinning" /> {t('searching', 'Searching...')}
                </>
              ) : (
                <>
                  <FiSearch /> {t('searchOperatorsButton', 'Search Operators')}
                </>
              )}
            </button>

            <button
              className="btn-outline"
              onClick={clearAllFilters}
              disabled={loading}
            >
              {t('clearAll', 'Clear All')}
            </button>
          </div>

          <div className="action-buttons">
            <button
              className="btn-icon"
              title={t('refresh', 'Refresh')}
              onClick={handleSearch}
              disabled={loading}
            >
              <FiRefreshCw />
            </button>
          </div>
        </div>
      </div>

      <div className="management-table-container" ref={resultsRef}>
        <div className="table-header">
          <h3>
            {searchPerformed ? t('searchResults', 'Search Results') : t('operators', 'Operators')}
            {searchPerformed && ` (${operators.length} ${t('found', 'found')})`}
          </h3>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FiLoader className="spinning" /> {t('loadingOperators', 'Loading operators...')}
          </div>
        ) : (
          <>
            {searchPerformed && operators.length === 0 ? (
              <div className="no-data-message">
                <FiUser size="3em" />
                <p>{t('noOperatorsFound', 'No operators found matching your search criteria.')}</p>
              </div>
            ) : (
              <table className="management-table">
                <thead>
                  <tr>
                    <th>{t('operatorNumber', 'Operator Number')}</th>
                    <th>{t('operatorName', 'Operator Name')}</th>
                    <th>{t('contactNumber', 'Contact Number')}</th>
                    <th>{t('status', 'Status')}</th>
                    <th>{t('state', 'State')}</th>
                    <th>{t('zone', 'Zone')}</th>
                    <th>{t('actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.map((operator) => (
                    <tr key={operator.operatorNumber}>
                      <td>
                        <div className="operator-code">
                          <span className="code-badge">{operator.operatorNumber}</span>
                        </div>
                      </td>
                      <td>
                        <div className="operator-info">
                          <strong>{operator.operatorName}</strong>
                        </div>
                      </td>
                      <td>{formatContactNumbers(operator.busOperatorContactNumber)}</td>
                      <td>
                        <button
                          className={`status-toggle ${operator.status ? 'active' : 'inactive'}`}
                          onClick={() => toggleStatus(operator.operatorNumber)}
                          title={operator.status ? t('clickToDeactivate', 'Click to deactivate') : t('clickToActivate', 'Click to activate')}
                        >
                          {operator.status ? (
                            <>
                              <FiCheckCircle /> {t('active', 'Active')}
                            </>
                          ) : (
                            <>
                              <FiXCircle /> {t('inactive', 'Inactive')}
                            </>
                          )}
                        </button>
                      </td>
                      <td>
                        <div className="state-info">
                          <span>{operator.stateCode || t('na', 'N/A')}</span>
                        </div>
                      </td>
                      <td>
                        <div className="zone-info">
                          <span>{operator.zoneCode || t('na', 'N/A')}</span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-view"
                            onClick={() => handleViewDetails(operator.operatorNumber)}
                            title={t('viewDetails', 'View Details')}
                          >
                            <FiEye />
                          </button>
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(operator.operatorNumber)}
                            title={t('edit', 'Edit')}
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(operator)}
                            title={t('deactivateOperator', 'Deactivate Operator')}
                            disabled={!operator.status}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      <style jsx>{`
        .management-container {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .management-header {
          margin-bottom: 2rem;
        }

        .management-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .management-header p {
          color: #666;
          font-size: 1rem;
        }

        .btn-back:hover {
          background: #e9ecef;
        }

        .search-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .search-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
        }

        .detailed-search-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .search-field-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-field-group label {
          font-size: 0.9rem;
          font-weight: 500;
          color: #555;
        }

        .search-input, .search-select {
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }

        .search-input:focus, .search-select:focus {
          outline: none;
          border-color: #667eea;
        }

        .search-input[readonly] {
          background: #f8f9fa;
          cursor: not-allowed;
        }

        .search-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .search-button-group {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-outline {
          background: white;
          color: #667eea;
          border: 1px solid #667eea;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-outline:hover:not(:disabled) {
          background: #667eea;
          color: white;
        }

        .btn-outline:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .management-table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
          padding: 1.5rem;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .table-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.2rem;
        }

        .management-table {
          width: 100%;
          border-collapse: collapse;
        }

        .management-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .management-table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
          color: #212529;
          vertical-align: middle;
        }

        .management-table tr:hover {
          background: #f8fafc;
        }

        .operator-code {
          display: flex;
          align-items: center;
        }

        .code-badge {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
        }

        .operator-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .operator-info strong {
          font-size: 1rem;
          color: #333;
        }

        .state-info, .zone-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .state-info span, .zone-info span {
          font-weight: 600;
          color: #333;
        }

        .state-name, .zone-name {
          font-size: 0.85rem;
          color: #666;
        }

        .status-toggle {
          border: none;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.2s;
        }

        .status-toggle.active {
          background: #d4edda;
          color: #155724;
        }

        .status-toggle.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-toggle:hover {
          transform: scale(1.05);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-view {
          background: #e3f2fd;
          color: #1976d2;
        }

        .btn-edit {
          background: #fff3e0;
          color: #f57c00;
        }

        .btn-delete {
          background: #ffebee;
          color: #d32f2f;
        }

        .btn-icon:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #666;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .no-data-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #666;
          text-align: center;
        }

        .no-data-message p {
          margin-top: 1rem;
          font-size: 1rem;
        }

        .alert {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-info {
          background: #e3f2fd;
          color: #1565c0;
          border: 1px solid #bbdefb;
        }

        .alert button {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .alert button:hover {
          opacity: 1;
        }

        @media (max-width: 1024px) {
          .management-container {
            padding: 1rem;
          }

          .management-table-container {
            overflow-x: auto;
          }

          .management-table {
            min-width: 1000px;
          }
        }

        @media (max-width: 768px) {
          .search-section {
            padding: 1rem;
          }

          .detailed-search-grid {
            grid-template-columns: 1fr;
          }

          .search-actions {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .search-button-group, .action-buttons {
            flex-direction: column;
            gap: 0.5rem;
          }

          .btn-primary, .btn-outline {
            width: 100%;
            justify-content: center;
          }

          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }

          .btn-icon {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default OperatorsManagement;